import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TextField from 'components/formik/TextField';
import { Field } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import Http from 'services/Http';
import { getOAuthTargetOrigin } from 'utils/webOrigin';
import { I18n } from 'utils/i18n';

// Google Picker API (inlined at webpack build from GOOGLE_PICKER_API_KEY; if unset, older builds had literal "undefined")
const GOOGLE_PICKER_API_KEY = process.env.GOOGLE_PICKER_API_KEY;

const isGooglePickerDeveloperKeyConfigured =
  typeof GOOGLE_PICKER_API_KEY === 'string' &&
  GOOGLE_PICKER_API_KEY.trim() !== '' &&
  GOOGLE_PICKER_API_KEY !== 'undefined';

const getStyles = theme => ({
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  description: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  oauthButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  connectedInfo: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  pickerButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
});

const GoogleSheetsConfig = ({ values, setFieldValue, errors, submitCount }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);

  const isConnected = !!values.configuration?.tokenId;
  const accessTokenError = submitCount > 0 && errors?.configuration?.tokenId;
  const sheetNameError = submitCount > 0 && errors?.configuration?.sheetName;

  const loadSheets = async (spreadsheetId, tokenId) => {
    try {
      setLoadingSheets(true);
      const { data } = await Http.post(`/v1/google-oauth/spreadsheets/${spreadsheetId}`, {
        tokenId,
      });
      setSheets(data.spreadsheetInfo?.sheets || []);
    } catch (error) {
      console.error('Error loading sheets:', error);
      setSheets([]);
    } finally {
      setLoadingSheets(false);
    }
  };

  // Load sheets when spreadsheet is selected
  useEffect(() => {
    if (values.configuration?.spreadsheetId && values.configuration?.tokenId) {
      loadSheets(values.configuration.spreadsheetId, values.configuration.tokenId);
    } else {
      setSheets([]);
    }
  }, [values.configuration?.spreadsheetId, values.configuration?.tokenId]);

  const handleGoogleAuth = async () => {
    try {
      setIsConnecting(true);

      // Get auth URL from backend
      const { data } = await Http.get('/v1/google-oauth/auth-url');

      // Open in new tab for better UX (removed noopener to allow communication)
      const authTab = window.open(data.authUrl, 'google-oauth');

      if (!authTab) {
        console.error('Unable to open Google OAuth window');
        setIsConnecting(false);
        return;
      }

      // Listen for message from auth tab
      const handleMessage = event => {
        if (event.source !== authTab) return;

        if (event.origin !== getOAuthTargetOrigin()) return;

        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          const { tokenId, userInfo } = event.data;

          // Update form values
          setFieldValue('configuration.tokenId', tokenId);
          setFieldValue('configuration.userEmail', userInfo.email);

          setIsConnecting(false);

          // Remove event listener
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          console.error('Google OAuth error:', event.data.error);
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      // Optional: Focus the auth tab
      if (authTab) {
        authTab.focus();
      }
    } catch (error) {
      console.error('Google OAuth error:', error);
      setIsConnecting(false);
    }
  };

  // Load Google APIs for frontend OAuth
  useEffect(() => {
    if (!window.gapi) {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('auth2:picker', () => {
          console.log('Google APIs loaded');
        });
      };
      document.head.appendChild(script);
    }
  }, []);

  const pickerCallback = async data => {
    if (data.action === window.google.picker.Action.PICKED) {
      const file = data.docs[0];

      // Set the spreadsheet info and clear sheet selection
      setFieldValue('configuration.spreadsheetId', file.id);
      setFieldValue('configuration.selectedFileName', file.name);
      setFieldValue('configuration.sheetName', '');
    }
  };

  const openFilePicker = async () => {
    if (!isGooglePickerDeveloperKeyConfigured) {
      console.error(
        'Google Picker developer key is not configured (build-time GOOGLE_PICKER_API_KEY)',
      );
      return;
    }
    if (!window.gapi || !values.configuration?.tokenId) {
      console.error('Google API not loaded or not authenticated');
      return;
    }

    try {
      // Get temporary access token for picker from backend
      const { data } = await Http.post('/v1/google-oauth/picker-token', {
        tokenId: values.configuration.tokenId,
      });

      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.SPREADSHEETS)
        .setOAuthToken(data.accessToken)
        .setDeveloperKey(GOOGLE_PICKER_API_KEY)
        .setOrigin(window.location.origin)
        .setCallback(pickerCallback)
        .setAppId(19162348773)
        .build();

      picker.setVisible(true);
    } catch (error) {
      console.error('Error getting picker token:', error);
    }
  };

  const handleDisconnect = () => {
    setFieldValue('configuration.tokenId', '');
    setFieldValue('configuration.userEmail', '');
    setFieldValue('configuration.spreadsheetId', '');
    setFieldValue('configuration.sheetName', '');
    setFieldValue('configuration.selectedFileName', '');
    setSheets([]);
  };

  return (
    <>
      <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Google Account')}</Typography>
      <Typography sx={styles.description}>
        {I18n.t('ChatFlowSettings.Connect your Google account to access spreadsheets')}
      </Typography>

      {isConnected ? (
        <Box sx={styles.connectedInfo}>
          <Typography variant="body2">
            {I18n.t('ChatFlowSettings.Connected as email', {
              email: values.configuration?.userEmail,
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDisconnect}
            style={{ marginTop: 8 }}
          >
            {I18n.t('ChatFlowSettings.Disconnect')}
          </Button>
        </Box>
      ) : (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoogleAuth}
            disabled={isConnecting}
            sx={styles.oauthButton}
          >
            {isConnecting
              ? I18n.t('ChatFlowSettings.Connecting...')
              : I18n.t('ChatFlowSettings.Connect Google Account')}
          </Button>
          {accessTokenError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {accessTokenError}
            </Typography>
          )}
        </>
      )}

      {isConnected && (
        <>
          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Spreadsheet')}</Typography>
          <Typography sx={styles.description}>
            {I18n.t(
              'ChatFlowSettings.Select a spreadsheet using Google Picker or enter ID manually',
            )}
          </Typography>

          {!isGooglePickerDeveloperKeyConfigured && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {I18n.t('ChatFlowSettings.Google Picker API key not configured')}
            </Typography>
          )}

          <Button
            variant="outlined"
            onClick={openFilePicker}
            sx={styles.pickerButton}
            disabled={!isGooglePickerDeveloperKeyConfigured}
          >
            {values.configuration?.selectedFileName
              ? I18n.t('ChatFlowSettings.Selected file', {
                  name: values.configuration.selectedFileName,
                })
              : I18n.t('ChatFlowSettings.Choose Spreadsheet')}
          </Button>

          {values.configuration?.spreadsheetId && (
            <>
              <FormControl
                fullWidth
                size="small"
                error={!loadingSheets && !!sheetNameError}
                margin="normal"
              >
                <InputLabel>
                  {loadingSheets
                    ? I18n.t('ChatFlowSettings.Loading sheets')
                    : I18n.t('ChatFlowSettings.Select sheet')}
                </InputLabel>
                <Select
                  size="small"
                  value={values.configuration?.sheetName || ''}
                  onChange={e => setFieldValue('configuration.sheetName', e.target.value)}
                  label={
                    loadingSheets
                      ? I18n.t('ChatFlowSettings.Loading sheets')
                      : I18n.t('ChatFlowSettings.Select sheet')
                  }
                  disabled={loadingSheets}
                  error={!loadingSheets && !!sheetNameError}
                >
                  {sheets.length === 0 ? (
                    <MenuItem value="" disabled>
                      <em>{I18n.t('ChatFlowSettings.No sheets found')}</em>
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem key="" value="">
                        <em>{I18n.t('ChatFlowSettings.Select sheet')}</em>
                      </MenuItem>,
                      ...sheets.map(sheet => (
                        <MenuItem key={sheet.title} value={sheet.title}>
                          {sheet.title}
                        </MenuItem>
                      )),
                    ]
                  )}
                </Select>
                {!loadingSheets && sheetNameError && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                    {sheetNameError}
                  </Typography>
                )}
              </FormControl>
              <Field
                component={TextField}
                name="configuration.spreadsheetId"
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                label={I18n.t('ChatFlowSettings.Spreadsheet ID')}
                helperText={I18n.t(
                  'ChatFlowSettings.Use the picker above or paste the Spreadsheet ID manually',
                )}
                size="small"
                margin="normal"
                slotProps={{
                  inputLabel: { shrink: !!values.configuration?.spreadsheetId },
                }}
              />
              <Field
                component={TextField}
                name="configuration.range"
                placeholder="A:E"
                label={I18n.t('ChatFlowSettings.Range')}
                helperText={I18n.t('ChatFlowSettings.Optional range')}
                size="small"
                margin="normal"
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default GoogleSheetsConfig;
